import { api, apiDelete, apiPost } from "@/shared/api/http";
import { useAuthStore } from "@/shared/lib/auth-store";

import type {
  AnalysisCreate,
  AnalysisFull,
  AnalysisShort,
  ClusterOut,
  ClusterPhraseOut,
  ClustersListResponse,
  EstimateIn,
  EstimateOut,
  ExpandRequest,
  ExpensePeriod,
  ExportRequest,
  OkResponse,
  PhrasesListResponse,
  PresetsResponse,
  ProjectCreate,
  ProjectCreateOut,
  ProjectDetailResponse,
  ProjectListResponse,
  ProjectLogOut,
  ProjectOut,
  ToggleClusterHiddenRequest,
  TogglePhraseHiddenRequest,
  WordstatExpenseOut,
} from "./types";

// ---------- estimate / presets ----------

export const estimateProject = (body: EstimateIn) =>
  apiPost<EstimateOut>("/api/projects/estimate", body);

export const getPresets = () => api<PresetsResponse>("/api/projects/presets");

// ---------- project CRUD ----------

export const createProject = (body: ProjectCreate) =>
  apiPost<ProjectCreateOut>("/api/projects", body);

export const getProjectsList = (params: {
  archived?: boolean | null;
  q?: string | null;
  limit?: number;
  offset?: number;
} = {}) => {
  const search = new URLSearchParams();
  if (params.archived !== undefined && params.archived !== null) {
    search.set("archived", String(params.archived));
  }
  if (params.q) search.set("q", params.q);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  return api<ProjectListResponse>(`/api/projects${qs ? `?${qs}` : ""}`);
};

export const getProjectDetail = (id: string) =>
  api<ProjectDetailResponse>(`/api/projects/${id}`);

export const getProjectClusters = (
  id: string,
  params: { show_hidden?: boolean } = {},
) => {
  const search = new URLSearchParams();
  if (params.show_hidden) search.set("show_hidden", "true");
  const qs = search.toString();
  return api<ClustersListResponse>(
    `/api/projects/${id}/clusters${qs ? `?${qs}` : ""}`,
  );
};

export const getClusterPhrases = (
  pid: string,
  cid: string,
  params: {
    q?: string;
    sort?: "count_desc" | "count_asc";
    limit?: number;
    offset?: number;
    show_hidden?: boolean;
  } = {},
) => {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  if (params.show_hidden) search.set("show_hidden", "true");
  const qs = search.toString();
  return api<PhrasesListResponse>(
    `/api/projects/${pid}/clusters/${cid}/phrases${qs ? `?${qs}` : ""}`,
  );
};

export const getProjectLogs = (id: string, limit = 100) =>
  api<ProjectLogOut[]>(`/api/projects/${id}/logs?limit=${limit}`);

export const cancelProject = (id: string) =>
  apiPost<ProjectOut>(`/api/projects/${id}/cancel`, {});

export const archiveProject = (id: string, on: boolean) =>
  apiPost<ProjectOut>(
    `/api/projects/${id}/${on ? "archive" : "unarchive"}`,
    {},
  );

export const deleteProject = (id: string) =>
  apiDelete<OkResponse>(`/api/projects/${id}`);

export const continueProject = (id: string) =>
  apiPost<ProjectOut>(`/api/projects/${id}/continue`, {});

export const expandProject = (id: string, body: ExpandRequest) =>
  apiPost<ProjectOut>(`/api/projects/${id}/expand`, body);

export const togglePhraseHidden = (
  projectId: string,
  clusterId: string,
  phraseId: number,
  body: TogglePhraseHiddenRequest,
) =>
  apiPost<ClusterPhraseOut>(
    `/api/projects/${projectId}/clusters/${clusterId}/phrases/${phraseId}/hidden`,
    body,
  );

export const toggleClusterHidden = (
  projectId: string,
  clusterId: string,
  body: ToggleClusterHiddenRequest,
) =>
  apiPost<ClusterOut>(
    `/api/projects/${projectId}/clusters/${clusterId}/hidden`,
    body,
  );

// Export — возвращает Blob (HTML), а не JSON. Скачивание делает вызывающий код.
export async function exportProjectFile(
  id: string,
  body: ExportRequest,
): Promise<Blob> {
  const token = useAuthStore.getState().token;
  const resp = await fetch(`/api/projects/${id}/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.blob();
}

// ---------- analyses ----------

export const getAnalysesList = (pid: string) =>
  api<AnalysisShort[]>(`/api/projects/${pid}/analyses`);

export const createAnalysis = (pid: string, body: AnalysisCreate) =>
  apiPost<AnalysisFull>(`/api/projects/${pid}/analyses`, body);

export const getAnalysis = (aid: string) =>
  api<AnalysisFull>(`/api/analyses/${aid}`);

export const enhanceAnalysis = (aid: string) =>
  apiPost<AnalysisFull>(`/api/analyses/${aid}/enhance`, {});

export const deleteAnalysis = (aid: string) =>
  apiDelete<OkResponse>(`/api/analyses/${aid}`);

// ---------- expense ----------

export const getWordstatExpense = (period: ExpensePeriod) =>
  api<WordstatExpenseOut>(`/api/me/wordstat-expense?period=${period}`);
